import { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ITagProps } from 'libs/design-system/src/types/tag/tag';
import { Button, Input } from '@unified-checkout/design-system';
import { ArrowUpRightFromSquare } from '@unified-checkout/design-system/icons/index';

import type { IMultipleCustomerDetailsProps } from '@app/dashboard/types/innerLayout/payment_links/single_quick_link';
import { emailOrPhoneValidation } from '@app/dashboard/utils/emailPhoneValidation';
import { normalizeEgyptPhone } from '@app/dashboard/utils/normalizeEgyptPhone';
import {
  INPUT_STATUS,
  MULTIPLE_CUSTOMERS_FIELDS,
  SINGLE_PAYMENTS_FIELDS,
} from '@app/dashboard/constants/payment-links/single-payments';
import { INNER_ROUTES } from '@app/dashboard/constants/inner-routes';

import styles from './multiple-customer.module.scss';

const MultipleCustomer = ({
  tags,
  setTags,
  fields,
}: IMultipleCustomerDetailsProps) => {
  const { t } = useTranslation();

  const [inputState, setInputState] = useState<{
    status: Lowercase<keyof typeof INPUT_STATUS>;
    message?: string;
  }>({ status: INPUT_STATUS.DEFAULT, message: '' });

  const paymentLimit = Number(
    fields[SINGLE_PAYMENTS_FIELDS.PAYMENT_LIMIT]?.value ?? 0
  );

  const handleTagAdd = (tag: ITagProps) => {
    const { isEgypt, normalized } = normalizeEgyptPhone(tag.text);

    let newTag = tag.text.includes('@') || !isEgypt ? tag.text : normalized;

    setTags((prevTags) => [...prevTags, { ...tag, text: newTag }]);
  };

  const handleTagRemove = (tagToRemove: ITagProps) => {
    setTags((prevTags) =>
      prevTags.filter(
        (tag) => tag.id !== tagToRemove.id || tag.text !== tagToRemove.text
      )
    );
  };

  const handleTagChange = (
    e: ChangeEvent<HTMLInputElement> | ITagProps
  ): boolean => {
    const value = 'text' in e ? e.text : e.target.value || '';
    const trimmedValue = value.trim();
    const { pattern, error_msg } = emailOrPhoneValidation();
    const isEmail = value.includes('@');
    const { normalized: normalizedPhone } = normalizeEgyptPhone(value);

    let finalStatus = {
      status: INPUT_STATUS.DEFAULT as Lowercase<keyof typeof INPUT_STATUS>,
      message: '',
    };

    if (trimmedValue && !pattern.test(trimmedValue)) {
      finalStatus = {
        status: INPUT_STATUS.ERROR,
        message: error_msg,
      };
    } else if (
      tags.some(
        (tag) => tag.text === (isEmail ? trimmedValue : normalizedPhone)
      )
    ) {
      finalStatus = {
        status: INPUT_STATUS.ERROR,
        message: t(
          'innerLayout.paymentLinksCreationPages.singlePaymentLink.sections.customerDetails.errors.duplicateCustomer'
        ),
      };
    } else if (tags.length > paymentLimit && paymentLimit !== 0) {
      finalStatus = {
        status: INPUT_STATUS.WARNING,
        message: t(
          'innerLayout.paymentLinksCreationPages.singlePaymentLink.sections.restrictions.errors.exceedPaymentNumbers'
        ),
      };
    } else {
      finalStatus = {
        status: INPUT_STATUS.DEFAULT,
        message: '',
      };
    }

    setInputState(finalStatus);
    return finalStatus.status !== INPUT_STATUS.ERROR;
  };

  useEffect(() => {
    return () => {
      setTags([]);
    };
  }, []);

  useEffect(() => {
    if (tags.length > paymentLimit && paymentLimit !== 0) {
      setInputState({
        status: INPUT_STATUS.WARNING,
        message: t(
          'innerLayout.paymentLinksCreationPages.singlePaymentLink.sections.restrictions.errors.exceedPaymentNumbers'
        ),
      });
    } else {
      setInputState({
        status: INPUT_STATUS.DEFAULT,
        message: '',
      });
    }
  }, [fields[SINGLE_PAYMENTS_FIELDS.PAYMENT_LIMIT]?.value, tags.length, t]);

  return (
    <div className={styles['multiple-customer']}>
      <Input
        type="tags"
        id={MULTIPLE_CUSTOMERS_FIELDS.MULTIPLE_CUSTOMERS}
        name={MULTIPLE_CUSTOMERS_FIELDS.MULTIPLE_CUSTOMERS}
        placeholder={t(
          'innerLayout.paymentLinksCreationPages.singlePaymentLink.sections.customerDetails.placeholders.emailOrPhone'
        )}
        label={t(
          'innerLayout.paymentLinksCreationPages.singlePaymentLink.sections.customerDetails.labels.shareLink'
        )}
        tags={tags}
        onTagAdd={handleTagAdd}
        onTagRemove={handleTagRemove}
        error={inputState.message}
        status={inputState.status}
        onChange={handleTagChange}
        tagValidator={handleTagChange}
      />
      <Button
        text={t(
          'innerLayout.paymentLinksCreationPages.singlePaymentLink.sections.customerDetails.uploadBulkCustomers'
        )}
        trailingIcon={ArrowUpRightFromSquare}
        size="md"
        variant="link"
        onClickHandler={() =>
          window.open(
            INNER_ROUTES.BULK_PAYMENT_LINKS,
            '_blank',
            'noopener,noreferrer'
          )
        }
      />
    </div>
  );
};

export default MultipleCustomer;
